import { useAuth } from "@clerk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Suspense, lazy, useEffect, useMemo, useState, type ReactElement, type ReactNode } from "react";
import { ArrowDown, ArrowUp, ChevronDown, Plus, Trash2 } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  toast,
} from "@ndd/ui";
import { StarterPromptGroups } from "@/components/v2/starter-prompt-groups";
import { ApiRequestError, authenticatedApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  type StarterPromptGroup,
  type StarterPromptLibraryData,
} from "../../../../shared/starter-prompts";
import { AdminGuard, AdminNav } from "./shared";

const EmojiPickerPanel = lazy(() => import("@/components/v2/emoji-picker-panel"));

const FIELD_BASE =
  "rounded-[10px] bg-sand-50 border-sand-200 text-foreground text-[13px] px-3 placeholder:text-sand-400 shadow-none";

/**
 * The admin edits labels and text; group keys are slugified from labels on
 * save (client-side), and the edit-first flag is derived server-side from the
 * text. The `id`s are client-only React keys so row state (popover open,
 * textarea focus) follows the logical row through reorders.
 */
interface DraftPrompt {
  id: string;
  text: string;
}
interface DraftGroup {
  id: string;
  emoji: string;
  label: string;
  description: string;
  prompts: DraftPrompt[];
}

const PREVIEW_RD_NAME = "Dana";

export function AdminStarterPrompts(): ReactElement {
  return (
    <AdminGuard>
      <TooltipProvider>
        <StarterPromptsClient />
      </TooltipProvider>
    </AdminGuard>
  );
}

function StarterPromptsClient(): ReactElement {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<DraftGroup[] | null>(null);
  const [baseline, setBaseline] = useState<string>("[]");
  const [showErrors, setShowErrors] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);

  const promptQuery = useQuery({
    queryKey: ["admin-starter-prompts"],
    queryFn: () =>
      authenticatedApi<{ data: StarterPromptLibraryData }>(
        getToken,
        "/api/admin/starter-prompts",
      ),
  });

  // Initialize drafts from the first load only: background refetches must not
  // clobber in-progress edits. Saves re-baseline from the PUT response below.
  useEffect(() => {
    if (promptQuery.data && drafts === null) {
      const initial = toDrafts(promptQuery.data.data.groups);
      setDrafts(initial);
      setBaseline(JSON.stringify(initial));
    }
  }, [promptQuery.data, drafts]);

  const save = useMutation({
    mutationFn: (nextGroups: StarterPromptGroup[]) =>
      authenticatedApi<{ data: StarterPromptLibraryData }>(
        getToken,
        "/api/admin/starter-prompts",
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ groups: nextGroups }),
        },
      ),
    onSuccess: (response) => {
      const saved = toDrafts(response.data.groups);
      setDrafts(saved);
      setBaseline(JSON.stringify(saved));
      setShowErrors(false);
      setConfirmingReset(false);
      void queryClient.invalidateQueries({ queryKey: ["admin-starter-prompts"] });
      void queryClient.invalidateQueries({ queryKey: ["starter-prompts"] });
      toast.success(
        response.data.source === "managed" ? "Starter prompts saved" : "Starter prompts reset to defaults",
      );
    },
    onError: (error) => toast.error(apiErrorMessage(error, "Failed to save starter prompts")),
  });

  const source = promptQuery.data?.data.source ?? "defaults";
  const isPending = save.isPending;
  const isDirty = drafts !== null && JSON.stringify(drafts) !== baseline;

  const previewGroups = useMemo(
    () => (drafts ?? []).map((draft) => toPreviewGroup(draft)),
    [drafts],
  );

  if (promptQuery.isError) {
    return (
      <div className="mx-auto max-w-[1360px] px-8 py-8">
        <AdminNav />
        <p className="text-sm text-sand-500">Unable to load starter prompts.</p>
      </div>
    );
  }

  function handleSave(): void {
    if (drafts === null) return;
    const problem = firstValidationProblem(drafts);
    if (problem) {
      setShowErrors(true);
      toast.error(problem);
      return;
    }
    save.mutate(toPayload(drafts));
  }

  function handleDiscard(): void {
    setDrafts(JSON.parse(baseline) as DraftGroup[]);
    setShowErrors(false);
  }

  return (
    <div className={cn("mx-auto max-w-[1360px] px-8 py-8", isDirty ? "pb-24" : "pb-8")}>
      <AdminNav />
      <div className="mb-6 flex items-start justify-between gap-6">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-sand-500 font-medium">
            Admin
          </div>
          <h1 className="text-2xl font-bold font-sans text-foreground">
            Starter prompts
          </h1>
          <p className="mt-1 max-w-[680px] text-sm text-sand-500">
            The prompt groups supervisors see when they start a New chat.
          </p>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-sand-600">
            <span className="flex items-center gap-2">
              <code className="rounded-md bg-info-soft px-1.5 py-0.5 font-mono text-[12px] font-medium text-info">
                {"{RD}"}
              </code>
              becomes the RD&apos;s first name
            </span>
            <span className="flex items-center gap-2">
              <code className="rounded-md bg-info-soft px-1.5 py-0.5 font-mono text-[12px] font-medium text-info">
                [topic]
              </code>
              bracketed slots ask supervisors to edit before sending
            </span>
          </div>
        </div>
        <Tooltip>
          {/* Disabled buttons swallow pointer events, so the tooltip needs a
              live wrapper to explain why the action is unavailable. */}
          <TooltipTrigger asChild>
            <span tabIndex={0}>
              <Button
                type="button"
                variant="ghost"
                disabled={isPending || source === "defaults"}
                onClick={() => setConfirmingReset(true)}
              >
                Use defaults…
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {source === "defaults"
              ? "Already using the built-in defaults — save this page to create a custom library first"
              : "Replace your custom library with the built-in prompts"}
          </TooltipContent>
        </Tooltip>
      </div>

      {source === "defaults" && drafts !== null && (
        <div className="mb-5 rounded-[10px] border border-warning/30 bg-warning-light px-4 py-3 text-sm text-bark">
          New chat is using the built-in defaults. Save this page to replace them with your own library.
        </div>
      )}

      {drafts === null ? (
        <p className="text-sm text-sand-500">Loading starter prompts...</p>
      ) : (
        // Spacing steps must exist in tailwind.config.ts's locked scale —
        // off-scale utilities (gap-7, pb-28) are silently dropped.
        <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(400px,0.85fr)]">
          <div className="space-y-4">
            {drafts.map((group, groupIndex) => (
              <GroupCard
                key={group.id}
                group={group}
                groupIndex={groupIndex}
                groupCount={drafts.length}
                showErrors={showErrors}
                onChange={(nextGroup) =>
                  setDrafts((current) => replaceAt(current ?? [], groupIndex, nextGroup))
                }
                onMove={(direction) =>
                  setDrafts((current) => moveItem(current ?? [], groupIndex, direction))
                }
                onRemove={() =>
                  setDrafts((current) => (current ?? []).filter((_, index) => index !== groupIndex))
                }
              />
            ))}
            <Button
              type="button"
              variant="outline"
              className="gap-1.5"
              onClick={() => setDrafts((current) => [...(current ?? []), newGroup()])}
            >
              <Plus className="h-4 w-4" />
              Add group
            </Button>
          </div>

          <section className="overflow-hidden rounded-[14px] border border-sand-200 bg-white shadow-md lg:sticky lg:top-6">
            <div className="border-b border-sand-100 px-6 py-4">
              <h2 className="font-sans text-[13px] font-semibold text-foreground">
                What supervisors see
              </h2>
              <p className="text-xs text-sand-400">
                New chat · updates as you edit · {"{RD}"} shown as &ldquo;{PREVIEW_RD_NAME}&rdquo;
              </p>
            </div>
            <div className="bg-sand-50 px-6 pb-8 pt-2">
              {previewGroups.length > 0 ? (
                <StarterPromptGroups
                  groups={previewGroups}
                  rdName={PREVIEW_RD_NAME}
                  onSelectPrompt={() => undefined}
                />
              ) : (
                <p className="py-10 text-center text-sm text-sand-500">
                  No groups — add one, or use defaults.
                </p>
              )}
            </div>
          </section>
        </div>
      )}

      {isDirty && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-sand-200 bg-white/95 backdrop-blur-sm">
          <div className="mx-auto flex max-w-[1360px] items-center gap-3 px-8 py-3">
            <span className="flex items-center gap-2 text-[13px] text-sand-600">
              <span aria-hidden className="h-[7px] w-[7px] rounded-full bg-sunlight-500" />
              Unsaved changes — supervisors still see the last saved version
            </span>
            <div className="flex-1" />
            <Button type="button" variant="ghost" disabled={isPending} onClick={handleDiscard}>
              Discard changes
            </Button>
            <Button type="button" disabled={isPending} onClick={handleSave}>
              {isPending ? "Saving..." : "Save prompts"}
            </Button>
          </div>
        </div>
      )}

      {confirmingReset && (
        <Dialog open={true} onOpenChange={(open) => !open && !isPending && setConfirmingReset(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Replace with built-in defaults?</DialogTitle>
              <DialogDescription>
                New chat goes back to the built-in prompt library for every supervisor, immediately.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-lg border border-error/30 bg-error-light px-3 py-2 text-sm text-bark">
              This permanently deletes your customized library
              {isDirty ? ", and any unsaved edits on this page are discarded too" : ""}.
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" disabled={isPending} onClick={() => setConfirmingReset(false)}>
                Cancel
              </Button>
              <Button type="button" variant="destructive" disabled={isPending} onClick={() => save.mutate([])}>
                {isPending ? "Resetting..." : "Use defaults"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function GroupCard({
  group,
  groupIndex,
  groupCount,
  showErrors,
  onChange,
  onMove,
  onRemove,
}: {
  group: DraftGroup;
  groupIndex: number;
  groupCount: number;
  showErrors: boolean;
  onChange: (group: DraftGroup) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}): ReactElement {
  return (
    <section className="rounded-xl border border-sand-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-foreground">
            {group.emoji} {group.label || "Untitled group"}
          </h2>
          <p className="text-xs text-sand-500">
            Group {groupIndex + 1} of {groupCount} · {group.prompts.length}{" "}
            {group.prompts.length === 1 ? "prompt" : "prompts"}
          </p>
        </div>
        <div className="flex gap-1">
          <IconButton label="Move group up" disabled={groupIndex === 0} onClick={() => onMove(-1)} icon={<ArrowUp className="h-4 w-4" />} />
          <IconButton label="Move group down" disabled={groupIndex === groupCount - 1} onClick={() => onMove(1)} icon={<ArrowDown className="h-4 w-4" />} />
          <IconButton label="Remove group" onClick={onRemove} icon={<Trash2 className="h-4 w-4" />} danger />
        </div>
      </div>

      <div className="grid grid-cols-[88px_1fr] gap-3">
        <Field label="Emoji">
          <EmojiPickerField
            value={group.emoji}
            invalid={showErrors && !group.emoji.trim()}
            onChange={(emoji) => onChange({ ...group, emoji })}
          />
        </Field>
        <Field label="Label">
          <Input
            value={group.label}
            onChange={(event) => onChange({ ...group, label: event.target.value })}
            className={cn(FIELD_BASE, "h-9", showErrors && !group.label.trim() && "border-error")}
          />
        </Field>
      </div>
      <Field label="Description — shown under the chips" className="mt-3">
        <Input
          value={group.description}
          onChange={(event) => onChange({ ...group, description: event.target.value })}
          className={cn(FIELD_BASE, "h-9", showErrors && !group.description.trim() && "border-error")}
        />
      </Field>

      <div className="mt-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-sand-500">Prompts</h3>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 gap-1.5"
            onClick={() => onChange({ ...group, prompts: [...group.prompts, { id: crypto.randomUUID(), text: "" }] })}
          >
            <Plus className="h-3.5 w-3.5" />
            Add prompt
          </Button>
        </div>
        {group.prompts.map((prompt, promptIndex) => (
          <PromptRow
            key={prompt.id}
            prompt={prompt}
            promptIndex={promptIndex}
            promptCount={group.prompts.length}
            showErrors={showErrors}
            onChange={(nextPrompt) => onChange({ ...group, prompts: replaceAt(group.prompts, promptIndex, nextPrompt) })}
            onMove={(direction) => onChange({ ...group, prompts: moveItem(group.prompts, promptIndex, direction) })}
            onRemove={() => onChange({ ...group, prompts: group.prompts.filter((_, index) => index !== promptIndex) })}
          />
        ))}
      </div>
    </section>
  );
}

function PromptRow({
  prompt,
  promptIndex,
  promptCount,
  showErrors,
  onChange,
  onMove,
  onRemove,
}: {
  prompt: DraftPrompt;
  promptIndex: number;
  promptCount: number;
  showErrors: boolean;
  onChange: (prompt: DraftPrompt) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}): ReactElement {
  return (
    <div className="rounded-lg border border-sand-100 bg-sand-50/50 p-3">
      <div className="flex items-start gap-3">
        <Textarea
          value={prompt.text}
          onChange={(event) => onChange({ ...prompt, text: event.target.value })}
          className={cn(FIELD_BASE, "min-h-[76px] flex-1 resize-y bg-white", showErrors && !prompt.text.trim() && "border-error")}
          placeholder="Prompt text"
        />
        <div className="flex gap-1">
          <IconButton label="Move prompt up" disabled={promptIndex === 0} onClick={() => onMove(-1)} icon={<ArrowUp className="h-4 w-4" />} />
          <IconButton label="Move prompt down" disabled={promptIndex === promptCount - 1} onClick={() => onMove(1)} icon={<ArrowDown className="h-4 w-4" />} />
          <IconButton label="Remove prompt" onClick={onRemove} icon={<Trash2 className="h-4 w-4" />} danger />
        </div>
      </div>
    </div>
  );
}

function EmojiPickerField({
  value,
  invalid,
  onChange,
}: {
  value: string;
  invalid: boolean;
  onChange: (emoji: string) => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Change emoji"
          className={cn(
            "flex h-9 items-center justify-center gap-1 rounded-[10px] border border-sand-200 bg-sand-50 text-[16px] transition-colors hover:bg-sand-100",
            invalid && "border-error",
          )}
        >
          <span>{value || "🙂"}</span>
          <ChevronDown className="h-3.5 w-3.5 text-sand-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Suspense
          fallback={
            <div className="flex h-[320px] w-[288px] items-center justify-center text-sm text-sand-400">
              Loading emoji…
            </div>
          }
        >
          <EmojiPickerPanel
            onSelect={(emoji) => {
              onChange(emoji);
              setOpen(false);
            }}
          />
        </Suspense>
      </PopoverContent>
    </Popover>
  );
}

function Field({ label, className, children }: { label: string; className?: string; children: ReactNode }): ReactElement {
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <Label className="text-xs text-sand-500">{label}</Label>
      {children}
    </div>
  );
}

function IconButton({ label, icon, onClick, disabled, danger }: { label: string; icon: ReactNode; onClick: () => void; disabled?: boolean; danger?: boolean }): ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          disabled={disabled}
          onClick={onClick}
          className={cn(
            "inline-flex h-8 w-8 items-center justify-center rounded-md text-sand-500 transition-colors hover:bg-sand-100 hover:text-foreground disabled:opacity-35",
            danger && "hover:bg-error-light hover:text-error",
          )}
        >
          {icon}
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function toDrafts(groups: StarterPromptGroup[]): DraftGroup[] {
  return groups.map((group) => ({
    id: crypto.randomUUID(),
    emoji: group.emoji,
    label: group.label,
    description: group.description,
    prompts: group.prompts.map((prompt) => ({ id: crypto.randomUUID(), text: prompt.text })),
  }));
}

/**
 * Preview keys reuse the draft ids so chip selection in the live preview
 * survives label edits and reorders; save payloads use readable label slugs.
 */
function toPreviewGroup(draft: DraftGroup): StarterPromptGroup {
  return {
    key: draft.id,
    emoji: draft.emoji,
    label: draft.label || "Untitled group",
    description: draft.description,
    prompts: draft.prompts.map((prompt) => ({ text: prompt.text })),
  };
}

// The edit-first `template` flag is intentionally absent here: the server
// derives it from the text on save, so the client never authors it.
function toPayload(drafts: DraftGroup[]): StarterPromptGroup[] {
  const usedKeys = new Set<string>();
  return drafts.map((draft) => ({
    key: uniqueKeyFor(draft.label, usedKeys),
    emoji: draft.emoji.trim(),
    label: draft.label.trim(),
    description: draft.description.trim(),
    prompts: draft.prompts.map((prompt) => ({ text: prompt.text.trim() })),
  }));
}

function uniqueKeyFor(label: string, usedKeys: Set<string>): string {
  const slug =
    label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "group";
  let key = slug;
  for (let suffix = 2; usedKeys.has(key); suffix += 1) {
    key = `${slug}-${suffix}`;
  }
  usedKeys.add(key);
  return key;
}

function newGroup(): DraftGroup {
  return {
    id: crypto.randomUUID(),
    emoji: "💬",
    label: "New group",
    description: "Describe when to use these prompts.",
    prompts: [{ id: crypto.randomUUID(), text: "Ask about {RD}." }],
  };
}

function firstValidationProblem(drafts: DraftGroup[]): string | null {
  if (drafts.length === 0) {
    return "Add at least one group before saving, or use defaults.";
  }
  for (const group of drafts) {
    const name = group.label.trim() || "an untitled group";
    if (!group.emoji.trim()) return `Pick an emoji for ${name}.`;
    if (!group.label.trim()) return "Every group needs a label.";
    if (!group.description.trim()) return `Add a description for ${name}.`;
    if (group.prompts.length === 0) return `Add at least one prompt to ${name}.`;
    if (group.prompts.some((prompt) => !prompt.text.trim())) {
      return `Fill in or remove the empty prompt in ${name}.`;
    }
  }
  return null;
}

function replaceAt<T>(items: T[], index: number, item: T): T[] {
  return items.map((current, currentIndex) => (currentIndex === index ? item : current));
}

function moveItem<T>(items: T[], index: number, direction: -1 | 1): T[] {
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= items.length) return items;
  const next = [...items];
  [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
  return next;
}

function apiErrorMessage(error: unknown, fallback: string): string {
  if (
    error instanceof ApiRequestError &&
    error.body &&
    typeof error.body === "object" &&
    typeof (error.body as { error?: unknown }).error === "string"
  ) {
    return (error.body as { error: string }).error;
  }
  return fallback;
}
